// LoginScreen.js - Pantalla de acceso con detección de sesión expirada
// Aloja las tres vistas del panel derecho ('login', 'forgot' y 'reset'): el flujo
// de recuperación no tiene rutas propias, se monta donde se renderice esta pantalla
// (el enlace del mail lleva a /task?reset={token} y PrivateRoute la pinta in-place)
import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Loader2, AlertCircle, Clock } from 'lucide-react';
import { m, useReducedMotion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import LoginBrandPanel from './LoginBrandPanel';
import { ForgotPasswordPanel, ResetPasswordPanel } from './PasswordPanels';
import './LoginScreen.css';

// El token del mail son 64 caracteres hex; cualquier otra cosa no se envía al API
const TOKEN_PATTERN = /^[a-f0-9]{64}$/;

const LoginScreen = () => {
  const { login, isLoading, error, clearError } = useAuth();
  const reduce = useReducedMotion();
  const [searchParams, setSearchParams] = useSearchParams();

  // Vista activa del panel derecho. Si la URL trae ?reset={token} se arranca
  // directamente en el formulario de contraseña nueva.
  const resetToken = searchParams.get('reset') || '';
  const [view, setView] = useState(resetToken ? 'reset' : 'login');

  // Volver al login quitando solo el ?reset de la URL: se conserva el pathname
  // actual (y el resto de query params) porque el servidor solo reescribe /task,
  // y mandar a nadie a /login dejaría una URL que da 404 al recargar.
  const backToLogin = () => {
    setView('login');
    if (resetToken) {
      const params = new URLSearchParams(searchParams);
      params.delete('reset');
      setSearchParams(params, { replace: true });
    }
  };

  // Estado del formulario
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState('');

  // ========================================================================
  // DETECTAR SI VIENE DE SESIÓN EXPIRADA (desde sessionStorage)
  // ========================================================================
  useEffect(() => {
    // Verificar si viene de sesión expirada desde sessionStorage
    const sessionExpired = sessionStorage.getItem('sessionExpired');
    
    if (sessionExpired === 'true') {
      setSessionExpiredMessage('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
      setFormErrors({});
      
      // Limpiar el flag de sessionStorage
      sessionStorage.removeItem('sessionExpired');
      
      // Limpiar el mensaje después de 12 segundos
      const timer = setTimeout(() => {
        setSessionExpiredMessage('');
      }, 12000);
      
      return () => clearTimeout(timer);
    }
  }, []);
  // ========================================================================

  // Limpiar errores cuando el usuario empiece a escribir.
  // El flag se calcula fuera del efecto: así el efecto no depende del objeto
  // formErrors (que cambia de identidad en cada render) y sale sin hacer nada
  // mientras no haya nada que limpiar, que es el caso normal al teclear.
  const hayErroresQueLimpiar = Boolean(error) || Object.keys(formErrors).length > 0;

  useEffect(() => {
    if (!hayErroresQueLimpiar || sessionExpiredMessage) return;

    const timer = setTimeout(() => {
      clearError();
      setFormErrors({});
    }, 100);
    return () => clearTimeout(timer);
  }, [formData, hayErroresQueLimpiar, clearError, sessionExpiredMessage]);

  // Manejar cambios en los inputs
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar errores al escribir
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
    
    // Limpiar mensaje de sesión expirada al empezar a escribir
    if (sessionExpiredMessage) {
      setSessionExpiredMessage('');
    }
  };

  // Validar formulario
  const validateForm = () => {
    const errors = {};
    
    if (!formData.email.trim()) {
      errors.email = 'El email es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'El formato del email no es válido';
    }
    
    if (!formData.password.trim()) {
      errors.password = 'La contraseña es obligatoria';
    } else if (formData.password.length < 3) {
      errors.password = 'La contraseña debe tener al menos 3 caracteres';
    }
    
    return errors;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar formulario
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setSessionExpiredMessage(''); // Limpiar mensaje al intentar login
    
    try {
      const result = await login({
        email: formData.email.trim(),
        password: formData.password
      });

      if (!result.success) {
        setFormErrors({ 
          submit: result.error || 'Error al iniciar sesión' 
        });
      }
      // Si el login es exitoso, el AuthContext redirigirá automáticamente
    } catch (error) {
      setFormErrors({ 
        submit: 'Error de conexión. Intenta de nuevo.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manejar Enter en los campos
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="login-split">
      <LoginBrandPanel />

      {/* Panel de formulario */}
      <div className="login-side">
        {/* key={view}: al cambiar de vista el bloque se re-monta y repite el fade */}
        <m.div
          key={view}
          className="login-form-col"
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
          animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
          transition={{ duration: reduce ? 0.2 : 0.5, ease: [0.22, 1, 0.36, 1] }}>

          {view === 'forgot' && (
            <ForgotPasswordPanel onBack={backToLogin} />
          )}

          {view === 'reset' && (
            <ResetPasswordPanel
              token={TOKEN_PATTERN.test(resetToken) ? resetToken : ''}
              onBack={backToLogin}
              onRequestAgain={() => setView('forgot')}
            />
          )}

          {view === 'login' && (
          <>
          <h2>Hola de nuevo</h2>
          <p className="login-lead">Accede a tu panel de Marketing Suite.</p>

          {/* ============================================================ */}
          {/* Banner de sesión expirada */}
          {/* ============================================================ */}
          {sessionExpiredMessage && (
            <div className="session-expired-banner">
              <Clock size={20} />
              <div className="banner-content">
                <strong>Sesión expirada</strong>
                <p>{sessionExpiredMessage}</p>
              </div>
            </div>
          )}
          {/* ============================================================ */}

          {/* Errores generales (solo si NO hay mensaje de sesión expirada) */}
          {(error || formErrors.submit) && !sessionExpiredMessage && (
            <div className="error-banner">
              <AlertCircle size={16} />
              <span>{error || formErrors.submit}</span>
            </div>
          )}

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="login-form">
            {/* Campo Email */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                onKeyPress={handleKeyPress}
                className={`form-input ${formErrors.email ? 'error' : ''}`}
                placeholder="tu@email.com"
                disabled={isSubmitting}
                autoComplete="email"
                autoFocus
              />
              {formErrors.email && (
                <span className="field-error">{formErrors.email}</span>
              )}
            </div>

            {/* Campo Contraseña */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Contraseña
              </label>
              <div className="password-input-container">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  onKeyPress={handleKeyPress}
                  className={`form-input ${formErrors.password ? 'error' : ''}`}
                  placeholder="••••••••"
                  disabled={isSubmitting}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isSubmitting}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {formErrors.password && (
                <span className="field-error">{formErrors.password}</span>
              )}
            </div>

            {/* Botón de submit */}
            <button
              type="submit"
              className="login-button"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? (
                <>
                  <Loader2 size={16} className="spinning" />
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar sesión'
              )}
            </button>
          </form>

          <button
            type="button"
            className="login-alt-link"
            onClick={() => setView('forgot')}
          >
            ¿Has olvidado tu contraseña?
          </button>
          </>
          )}

        </m.div>
      </div>
    </div>
  );
};

export default LoginScreen;